<template>
    <v-card>
        <v-card-text class="grey lighten-3">
            <v-form v-model="isFormValid" @submit.prevent>
                <v-layout
                    v-for="question in questionsList"
                    align-center
                    :class="{'wrap': $vuetify.breakpoint.smAndDown}"
                    :key="question.id"
                >
                    <template v-if="question.showQuestion">
                        <v-flex sm12 md7 class="pb-5">
                            <!-- questions -->
                            <span class="title mr-6">
                                <span v-if="question.required">
                                    *
                                </span>
                                <span v-html="question[`question${upperLocale}`]" />
                            </span>
                        </v-flex>
                        <v-flex sm12 md5 align-self-start>
                            <!-- datePicker field component -->
                            <dynamic-date-input
                                v-if="question.input === 'DatePicker'"
                                :question="question"
                                ref="dynamicDateInput"
                                @updateQuestionResponse="validateResponse($event)"
                            />

                            <!-- text field component -->
                            <dynamic-text-field
                                v-if="question.input === 'TextField'"
                                :question="question"
                                ref="dynamicTextField"
                                @updateQuestionResponse="validateResponse($event)"
                            />

                            <!--dropdown field component -->
                            <dynamic-dropdown-field
                                v-if="question.input === 'Dropdown'"
                                :question="question"
                                ref="DynamicDropdownField"
                                @updateQuestionResponse="validateResponse($event)"
                            />
                        </v-flex>
                    </template>
                </v-layout>

                <template v-if="alerts.length">
                    <v-alert
                        v-for="alert in alerts"
                        :value="true"
                        :type="alert.type"
                        class="mb-2"
                        tabindex="0"
                        :key="alert.key"
                    >
                        <div class="alert-custom-style" v-html="alert.message[`${upperLocale}`]" />
                    </v-alert>
                </template>
            </v-form>
        </v-card-text>
        <v-card-actions class="grey lighten-3">
            <v-layout class="row">
                <v-flex xs12 class="text-center">
                    <next-button
                        data-testid="continue-select-service"
                        :loading="loading"
                        :disabled="loading || !isFormValid || hasAlerts"
                        id="dynamicServiceFiltersNextButton"
                        @nextStep="validateQuestions"
                    />
                </v-flex>
            </v-layout>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import DynamicDateInput from '@/components/steppers/DynamicFormFields/DynamicDateInput';
import DynamicTextField from '@/components/steppers/DynamicFormFields/DynamicTextField';
import DynamicDropdownField from '@/components/steppers/DynamicFormFields/DynamicDropdownField';
import { differenceInCalendarDays, isAfter, format, parse } from 'date-fns';
import {
    cloneDeep, compact, flatMap, findIndex, forEach,
    indexOf, intersection, map, size, some, toUpper,
    find
} from 'lodash';
import * as Sentry from '@sentry/browser';
import { MetadataEnum } from '@/enums';
import NextButton from '@/components/buttons/NextButton.vue';
import { setFocusForAccessibility } from '@/plugins/accessibility';

export default {
    name: 'step-dynamic-service-filters-questions',
    props: {
        dynamicServiceFilters: { type: Object, required: true },
    },
    components: {
        DynamicDateInput,
        DynamicTextField,
        DynamicDropdownField,
        NextButton
    },
    computed: {
        ...mapGetters({
            locale: 'locale',
            upperLocale: 'upperLocale',
            getEstablishmentId: 'establishment/getEstablishmentId',
        }),
    },
    data: () => ({
        // Loading
        loading: false,
        // Form
        isFormValid: false,
        // Alerts
        alerts: [ ],
        filteredServiceTemplateIds: [],
        dependantAnswers: null,
        questionsList: [],
        boolQuestionCondition: false,
        hasAlerts: false,
        localDynamicMetasResponses: [],
    }),
    created() {
        this.createQuestionsList();
    },
    methods: {
        ...mapActions({
            filterInstantiateServiceWithServiceTemplateIds: 'services/filterInstantiateServiceWithServiceTemplateIds',
        }),
        ...mapMutations({
            setDynamicFilterMetadataValues: 'services/SET_DYNAMIC_FILTER_METADATA_VALUES',
        }),
        /**
         * Create list of questions, add showQuestion = false to all questions except the first one
         */
        createQuestionsList() {
            this.questionsList = compact(flatMap(cloneDeep(this.dynamicServiceFilters.questions), filter => {
                filter.showQuestion = filter.dependantAnswers === null;
                filter.response = null;
                if (filter.id === 1) {
                    filter.showQuestion = true;
                }
                return filter;
            }));
        },

        /**
         * Update list of questions, update showQuestion according to "dependantAnswers"
         * @param questionIndex
         * @param questionId
         */
        updateQuestionsList(questionIndex, questionId) {
            forEach(this.questionsList, (question, key, obj) => {
                if (question.dependantAnswers) {
                    let show = true;
                    forEach(question.dependantAnswers, (answerValue, subkey) => {
                        if (show) {
                            let index = findIndex(this.questionsList, ['id', parseInt(subkey)]);
                            if(parseInt(answerValue) !== parseInt(this.questionsList[index].response)) {
                                show = false;
                            }
                        }
                    });
                    obj[key].showQuestion = show;
                }
                else {
                    obj[key].showQuestion = true;
                }
            });
        },

        /**
         * Prepare alerts messages
         * @param questionIndex
         * @param questionId
         */
        prepareAlerts(questionIndex, questionId) {
            const alerts = this.dynamicServiceFilters.alerts;
            this.hasAlerts = false;
            let invalidAlerts = [];

            forEach(alerts, (alert, key, obj) => {
                return compact(map(alert.dependantAnswers, (dependantAnswers, subkey) => {
                    let question = find(this.questionsList, q => q.id === parseInt(subkey));
                    let response = question.response;

                    if(!question) {
                        const err = new Error('Question not found for alert condition');
                        Sentry.withScope(scope => {
                            scope.setExtra('alertName', alert.name);
                            scope.setExtra('dependantAnswer', subkey);
                            scope.setFingerprint(['missing-question-alert-condition']);
                            Sentry.captureException(err);
                        });
                        return;
                    }
                    switch (question.type) {
                        case 'Date':
                            const periods = parseInt(this.getPeriodsTypeCondition(response, question.typeSpecificType));
                            if(this.dateConditionChecker(periods, dependantAnswers.value, dependantAnswers.type) && !find(invalidAlerts, name => name === alert.name)) {
                                this.addAlert(alert.name, {
                                    Fr: alert['descriptionFr'],
                                    En: alert['descriptionEn'],
                                }, 'warning');
                                this.hasAlerts = true;
                            }
                            else {
                                this.removeAlert(alert.name);
                                invalidAlerts.push(alert.name);
                            }
                            break;
                        case 'Boolean':
                            if(dependantAnswers.value === !!parseInt(response) && !find(invalidAlerts, name => name === alert.name)) {
                                this.addAlert(alert.name, {
                                    Fr: alert['descriptionFr'],
                                    En: alert['descriptionEn'],
                                }, 'warning');
                                this.hasAlerts = true;
                            }
                            else {
                                this.removeAlert(alert.name);
                                invalidAlerts.push(alert.name);
                            }
                            break;
                    }
                    return null;
                }));
            });
        },

        /**
         * Validate response receive from child component
         * @param event
         */
        validateResponse(event) {
            if (event) {
                const response = event.dynamicResponse;
                const dynamicQuestionId = event.questionId;
                const customId = event.customId ?? null;
                const questionIndex = findIndex(this.dynamicServiceFilters.questions, ['id', dynamicQuestionId]);
                const serviceTemplateIds = this.dynamicServiceFilters.serviceTemplates;
                this.questionsList[questionIndex].response = response;

                this.validateServiceTemplates(serviceTemplateIds);
                this.updateQuestionsList(questionIndex, dynamicQuestionId);
                this.prepareAlerts(questionIndex, dynamicQuestionId);
                this.setCustomFocus(customId);
            }
        },

        /**
         * Validate services templates
         * @param serviceTemplateIds
         */
        validateServiceTemplates(serviceTemplateIds) {
            const serviceTemplatesArrays = [];
            let questionServiceTemplatesIds = [];
            forEach(this.questionsList, (question, key, obj) => {
                if (question.conditions && question.showQuestion) {
                    switch (question.type) {
                        case 'Date':
                            questionServiceTemplatesIds = flatMap(this.filterDateConditions(question.response, question.conditions, question.typeSpecificType));
                            break;
                        case 'Boolean':
                            const response = question.response === '1'; // question.response === '1' ? true : false
                            questionServiceTemplatesIds = this.filterBoolConditions(response, question.conditions);
                            break;
                    }
                    serviceTemplatesArrays.push(questionServiceTemplatesIds);
                }
            });
            this.filteredServiceTemplateIds = intersection(serviceTemplateIds, ...serviceTemplatesArrays);
        },

        /**
         * Build array of metas object to return to parent
         */
        buildMetaData() {
            forEach(this.questionsList, (question, key, obj) => {
                if (question.associatedMetadata) {
                    this.localDynamicMetasResponses.push({
                        'associatedMetadata': question.associatedMetadata,
                        'associatedMetadataId': MetadataEnum[toUpper(question.associatedMetadata)],
                        'value': question.response,

                    });
                }
            });
        },

        /**
         * Emits the next step event to parent
         */
        async nextStep() {
            if (size(this.filteredServiceTemplateIds)) {
                this.localDynamicMetasResponses = [];
                this.buildMetaData();
                this.temporaryFilterForSixMonthVaccine();
                await this.filterInstantiateServiceWithServiceTemplateIds(this.filteredServiceTemplateIds);
                this.setDynamicFilterMetadataValues(this.localDynamicMetasResponses);
                this.$emit('nextStep');
            }
            else {
                const message = this.$i18n.t('appt.errors.dynamicServiceFilters');
                this.alerts.push({ key: 'noResult', type: 'warning', message: message });
            }
        },

        /**
         * Return serviceTemplates for Boolean response
         */
        filterBoolConditions(response, conditions) {
            const index = findIndex(conditions, ['value', response]);
            return conditions[index].serviceTemplates;
        },

        /**
         * Return serviceTemplates for date response
         */
        filterDateConditions(response, conditions, typeSpecificType) {
            return compact(map(conditions, (condition) => {
                // get the numbers of days
                const periods = parseInt(this.getPeriodsTypeCondition(response, typeSpecificType));
                return this.dateConditionChecker(periods, condition.value, condition.type)
                    ? condition.serviceTemplates
                    : [];
            }));
        },

        /**
         * Return the check value for a date condition
         * @param periods -> calculated nb of days
         * @params value -> value to compare with the periods
         * @params type -> type of comparaison to make
         */
        dateConditionChecker(periods, value, type) {
            if (type === '<' && periods < parseInt(value)) { return true; }
            else if (type === '>=' && periods >= parseInt(value)) { return true; }
            else if (type === '<=' && periods <= parseInt(value)) { return true; }
            else if (type === '>' && periods > parseInt(value)) { return true; }
            else if (type === '=' && periods === parseInt(value)) { return true; }
            else if (type === '!=' && periods !== parseInt(value)) { return true; }
            else if (type === '<>' && periods &&
                    periods >= value[0].min &&
                    periods <= value[0].max) { return true; }

            return false;
        },

        /**
         * Calculate the number of days between 2 dates (future or past)
         * @param date
         * @param typeSpecificType
         */
        getPeriodsTypeCondition(date, typeSpecificType) {
            // date in the futur
            let dateStart = date;
            let dateStop = format(Date.now(), 'YYYY-MM-DD');
            if (typeSpecificType === 'birthdate') {
                // date in the past
                dateStart = dateStop;
                dateStop = date;
            }
            return differenceInCalendarDays(dateStart, dateStop);
        },

        /**
         * Event handler for 'add-alert'
         * @param key
         * @param message
         * @param type
         */
        addAlert(key, message, type) {
            this.alerts = [];
            if (!some(this.alerts, ['key', key])) {
                this.alerts.push({ key: key, type: type, message: message });
            }
        },

        /**
         * Event handler for 'remove-alert'
         * @param key
         */
        removeAlert(key) {
            const index = findIndex(this.alerts, ['key', key]);
            if (index !== -1) {
                this.alerts.splice(index, 1);
            }
        },

        /**
         * Call validate functions of child components
         */
        async validateQuestions() {
            if (!this.alerts.length) {
                await this.nextStep();
            }
        },

        /**
         * @description Pour les mois de juin a decembre 2019, retirer l'offre de service de vaccin 6 mois pour les enfants née après le 01 juin 2019
         */
        temporaryFilterForSixMonthVaccine() {
            // TODO : Supprimer cette fonction après en janvier 2020 et supprimer le service de vaccin 6 mois
            const maxdate = parse('2019-06-01');
            const vaccineServiceId = 261;

            const serviceIndex = indexOf(this.filteredServiceTemplateIds, vaccineServiceId);
            if(serviceIndex + 1) {
                const answerIndex = findIndex(this.localDynamicMetasResponses, { associatedMetadata: 'birthday' });
                if(answerIndex + 1) {
                    const birthday = parse(this.localDynamicMetasResponses[answerIndex].value);
                    if(isAfter(birthday, maxdate)) {
                        // remove 6 month vaccin service from the array
                        this.filteredServiceTemplateIds.splice(serviceIndex, 1);
                    }
                }
            }
        },
        /**
         * When an answer is clicked
         */
        setCustomFocus(customId = null) {
            if (customId !== null) {
                setFocusForAccessibility(customId, 0);
            }
        },
    },
};
</script>

<style lang="scss">
@import './src/assets/scss/variables';
.alert-custom-style {
    ol, ul {
        list-style-type: initial;
    }

    p {
        padding-bottom: 0.5em;
    }

    ul {
        margin-left: 2em;
        margin-bottom: 1em;
    }

    a {
        color: white;
    }
}
</style>
